import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { AppDownloadButton } from "@/components/AppDownloadButton";
import { ModernLogo } from "@/components/ModernLogo";
import { 
  Shield, 
  CheckCircle, 
  Users, 
  Clock, 
  CreditCard, 
  Award, 
  FileCheck, 
  Phone, 
  Mail, 
  MapPin,
  ArrowLeft,
  Star,
  TrendingUp,
  UserCheck,
  Lock,
  Zap
} from "lucide-react";
import { useSEO } from "@/hooks/useSEO";

export const AboutUs = () => {
  // SEO optimization for About Us page
  useSEO({
    title: "About Sortable - Compare Local Tradespeople & Quotes",
    description: "Learn about Sortable's mission to help customers compare quotes from verified, insured UK tradesmen. Get up to 5 free quotes and compare reviews.",
    keywords: "about sortable, verified tradesmen, insured contractors, safe tradesman platform, UK property maintenance, professional tradesmen, tradesman verification process, contractor insurance validation, insurance validation, safety standards, quality assurance, customer protection, secure payment system, professional certification, trade qualifications, regulatory compliance, industry standards, business ethics, transparency, accountability, customer satisfaction, service excellence, reliable contractors, trustworthy platform, ethical business practices, consumer rights, fair trading, dispute resolution, complaint handling, feedback system, continuous improvement, innovation, technology platform, digital transformation, user experience, mobile accessibility, 24/7 support, national coverage, local expertise, community focus, environmental responsibility, sustainable practices, social impact, economic contribution, job creation, skills development, training programs, professional development, career advancement, business growth, market leadership, competitive advantage, brand reputation, customer loyalty, repeat business, referral network",
    canonical: "https://sortable.uk/about-us",
    ogImage: "https://sortable.uk/hero-tradesman.jpg",
    structuredData: {
      "@context": "https://schema.org",
      "@type": "AboutPage",
      "mainEntity": {
        "@type": "Organization",
        "name": "Sortable",  
        "description": "The UK's most trusted platform connecting customers with verified, professional tradesmen",
        "url": "https://sortable.uk",
        "founder": {
          "@type": "Organization",
          "name": "Realise Property Maintenance Ltd"
        },
        "foundingDate": "2024",
        "numberOfEmployees": "50-100",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "GB"
        }
      }
    }
  });

  return (
    <div className="min-h-screen bg-gradient-subtle">
      {/* Modern Header */}
      <header className="relative bg-gradient-to-r from-background via-background/95 to-background backdrop-blur-lg border-b border-border/50 shadow-elegant sticky top-0 z-50">
        <div className="absolute inset-0 bg-gradient-hero opacity-5"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-2 md:py-3">
            {/* Brand Section */}
            <div className="flex items-center gap-2 md:gap-4">
              <div className="relative">
                <ModernLogo size="md" showTagline={false} />
              </div>
              
              {/* Download App Button */}
              <div className="hidden md:block">
                <AppDownloadButton variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200" />
              </div>
            </div>

            {/* Navigation Actions */}
            <div className="flex items-center gap-1 md:gap-3">
              <Button asChild variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200">
                <Link to="/">
                  <ArrowLeft className="h-3 w-3 md:h-4 md:w-4 md:mr-2" />
                  <span className="hidden md:inline">Back to Platform</span>
                  <span className="md:hidden sr-only">Back</span>
                </Link>
              </Button>
              
              {/* Mobile Download Button */}
              <div className="md:hidden">
                <AppDownloadButton variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200 px-2" />
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl font-bold text-foreground mb-6">
            About Sortable
          </h1>
          <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
            The UK's trusted platform for comparing local tradespeople. We help you get up to 5 free quotes 
            from verified, professional tradesmen in your area. Compare prices, read reviews, and hire with confidence - 
            making it easy to find the right tradesperson for your job.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Badge variant="secondary" className="text-sm px-4 py-2">
              <Shield className="h-4 w-4 mr-2" />
              Fully Insured Tradesmen
            </Badge>
            <Badge variant="secondary" className="text-sm px-4 py-2">
              <Clock className="h-4 w-4 mr-2" />
              Multiple Free Quotes
            </Badge>
            <Badge variant="secondary" className="text-sm px-4 py-2">
              <CreditCard className="h-4 w-4 mr-2" />
              Secure Payment Protection
            </Badge>
          </div>
        </div>
      </section>

      {/* Our Services */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Our Complete Range of Services</h2>
            <p className="text-lg text-muted-foreground">
              From small repairs to major renovations, we help you compare quotes and find the right tradesman for every job
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { icon: Zap, title: "Electrical", description: "Compare quotes from certified electricians for installations, repairs, and safety inspections" },
              { icon: Users, title: "Plumbing", description: "Get quotes from experienced plumbers and heating engineers in your area" },
              { icon: Award, title: "Carpentry", description: "Skilled carpenters for bespoke furniture and structural work" },
              { icon: Shield, title: "Gas Services", description: "Gas Safe registered engineers for all gas appliances" },
              { icon: CheckCircle, title: "Painting & Decorating", description: "Professional painters for interior and exterior projects" },
              { icon: TrendingUp, title: "Building & Construction", description: "Qualified builders for extensions, conversions, and repairs" }
            ].map((service, index) => (
              <Card key={index} className="shadow-elegant hover:shadow-glow transition-shadow">
                <CardHeader className="text-center">
                  <service.icon className="h-12 w-12 text-primary mx-auto mb-4" />
                  <CardTitle className="text-lg">{service.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-muted-foreground text-center">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Standards */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Uncompromising Safety Standards</h2>
            <p className="text-lg text-muted-foreground">
              Your safety is our priority. Every tradesman undergoes our comprehensive verification process
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-green-600" />
                  Insurance & Certification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Public Liability Insurance</p>
                    <p className="text-sm text-muted-foreground">Minimum £2M coverage verified and monitored</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Trade Certifications</p>
                    <p className="text-sm text-muted-foreground">All relevant qualifications verified annually</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Gas Safe Registration</p>
                    <p className="text-sm text-muted-foreground">Current registration checked for all gas engineers</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <UserCheck className="h-6 w-6 text-blue-600" />
                  Insurance Verification
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Identity Verification</p>
                    <p className="text-sm text-muted-foreground">Multi-stage identity and address verification</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Reference Checks</p>
                    <p className="text-sm text-muted-foreground">Professional and personal references validated</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Ongoing Monitoring */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Continuous Quality Monitoring</h2>
            <p className="text-lg text-muted-foreground">
              We don't just vet once - we continuously monitor our tradesmen to maintain the highest standards
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="shadow-elegant">
              <CardHeader className="text-center">
                <FileCheck className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Regular Reviews</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Annual insurance renewal checks
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Quarterly certification updates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Ongoing performance monitoring
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-elegant">
              <CardHeader className="text-center">
                <Star className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Customer Feedback</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Real-time job completion ratings
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Detailed quality assessments
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    24/7 customer support monitoring
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="shadow-elegant">
              <CardHeader className="text-center">
                <Lock className="h-12 w-12 text-primary mx-auto mb-4" />
                <CardTitle>Compliance Tracking</CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Automated compliance alerts
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Health & safety training updates
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    Industry regulation monitoring
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Financial Security */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Complete Financial Security</h2>
            <p className="text-lg text-muted-foreground">
              Advanced payment protection and cashflow security for both customers and tradesmen
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-6 w-6 text-green-600" />
                  Customer Protection
                </CardTitle>
                <CardDescription>Your money is safe with our secure payment system</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Escrow Payment System</p>
                    <p className="text-sm text-muted-foreground">Funds held securely until job completion</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">256-bit SSL Encryption</p>
                    <p className="text-sm text-muted-foreground">Bank-level security for all transactions</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Dispute Resolution</p>
                    <p className="text-sm text-muted-foreground">Independent mediation service available</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Money Back Guarantee</p>
                    <p className="text-sm text-muted-foreground">Full refund if work doesn&apos;t meet standards</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-elegant">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="h-6 w-6 text-blue-600" />
                  Tradesman Cashflow
                </CardTitle>
                <CardDescription>Reliable income and prompt payment processing</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Guaranteed Payment</p>
                    <p className="text-sm text-muted-foreground">Payment secured before work begins</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Next-Day Transfers</p>
                    <p className="text-sm text-muted-foreground">Fast payment processing upon job completion</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">No Hidden Fees</p>
                    <p className="text-sm text-muted-foreground">Transparent pricing with no surprise charges</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  <div>
                    <p className="font-medium">Invoice Management</p>
                    <p className="text-sm text-muted-foreground">Automated invoicing and payment tracking</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Why Choose Sortable?</h2>
            <p className="text-lg text-muted-foreground">
              The trusted choice for thousands of customers and professional tradesmen across the UK
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="bg-primary/10 rounded-full p-6 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Clock className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Fast Response</h3>
              <p className="text-sm text-muted-foreground">Guaranteed fast response or your money back</p>
            </div>

            <div className="text-center">
              <div className="bg-primary/10 rounded-full p-6 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Award className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Verified Tradesmen</h3>
              <p className="text-sm text-muted-foreground">Every tradesman fully vetted, insured, and certified</p>
            </div>

            <div className="text-center">
              <div className="bg-primary/10 rounded-full p-6 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Shield className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">Secure Platform</h3>
              <p className="text-sm text-muted-foreground">Advanced security and payment protection for all users</p>
            </div>

            <div className="text-center">
              <div className="bg-primary/10 rounded-full p-6 w-20 h-20 mx-auto mb-4 flex items-center justify-center">
                <Users className="h-10 w-10 text-primary" />
              </div>
              <h3 className="font-semibold mb-2">24/7 Support</h3>
              <p className="text-sm text-muted-foreground">Round-the-clock customer support and assistance</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Information */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Get In Touch</h2>
            <p className="text-lg text-muted-foreground">
              Have questions? Our team is here to help you get started
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="text-center shadow-elegant">
              <CardContent className="p-6">
                <Phone className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Phone Support</h3>
                <p className="text-muted-foreground">0800 043 8880</p>
                <p className="text-sm text-muted-foreground mt-1">24/7 Emergency Line</p>
              </CardContent>
            </Card>

            <Card className="text-center shadow-elegant">
               <CardContent className="p-6">
                 <Mail className="h-8 w-8 text-primary mx-auto mb-4" />
                 <h3 className="font-semibold mb-2">Email Support</h3>
                 <p className="text-muted-foreground">realise@post.com</p>
                 <p className="text-sm text-muted-foreground mt-1">Response within 1 hour</p>
               </CardContent>
            </Card>

            <Card className="text-center shadow-elegant">
              <CardContent className="p-6">
                <MapPin className="h-8 w-8 text-primary mx-auto mb-4" />
                <h3 className="font-semibold mb-2">Head Office</h3>
                <p className="text-muted-foreground">London, UK</p>
                <p className="text-sm text-muted-foreground mt-1">Serving all UK regions</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-primary text-primary-foreground">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Ready to Experience the Difference?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of satisfied customers and professional tradesmen on the UK&apos;s most trusted platform
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button asChild size="lg" variant="secondary">
              <Link to="/">
                Get Started as Customer
              </Link>
            </Button>
            <Button asChild size="lg" variant="outline" className="text-primary">
              <Link to="/">
                Join as Tradesman
              </Link>
            </Button>
            <AppDownloadButton variant="outline" size="lg" className="text-primary border-white hover:bg-white/10" />
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-secondary text-secondary-foreground py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div className="space-y-4">
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground">
                The UK's most trusted platform for finding verified, professional tradesmen.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/success-stories" className="hover:text-primary">Success Stories</Link></li>
                <li><Link to="/" className="hover:text-primary">Get Started</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Tradesmen</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Platform</Link></li>
                <li><Link to="/pricing" className="hover:text-primary">Pricing</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Contact</h4>
              <div className="space-y-2 text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <span>08000438880</span>
                </div>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <span>realise@post.com</span>
                </div>
              </div>
            </div>
          </div>
          <div className="border-t border-border/50 mt-8 pt-8 text-center text-muted-foreground">
            <p>&copy; 2025 Sortable. All rights reserved. | Registered in England & Wales | Fully insured and regulated</p>
          </div>
        </div>
      </footer>
    </div>
  );
};